import Load from 'components/Load';
import useIsMobile from 'hooks/useIsMobile';
import {
  NavigationalLayoutElement,
  type NavigationalLayoutProps,
} from 'modules/app/components/NavigationalLayout';
import AppBottomPushDown from 'modules/common/Navbar/PushDown/AppBottomPushDown';
import Notfound from 'modules/errorpages/Notfound';
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { RouteDescriptor, createRouteHandle } from 'routing/types';
import basePath from 'utils/basePath';
import { relativePathFromBase } from 'utils/relativePathFromBase';

const HostExperiencesPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-experiences-overview" */ './containers/HostExperiencesPage'
    ),
);
const HostRecommendationsPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-recommendations-overview" */ './containers/HostRecommendationsPage'
    ),
);
const AsyncPausedModal = Load(
  () =>
    import(
      /* webpackChunkName: "host-experiences-overview" */ './components/Modals/PauseModal'
    ),
);
const OriginalsDetailsPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-experiences-originals-details" */ './containers/OriginalsDetailsPage'
    ),
);
const RecommendationDetailsPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-experiences-recommendation-details" */ './containers/RecommendationDetailsPage'
    ),
);

const hostExperiencesBase = `${basePath}/host/experiences`;
type ChildRouteOptions = Omit<NavigationalLayoutProps, 'component'> & {
  path?: string;
  index?: boolean;
};

const createChildRoute = (
  descriptor: RouteDescriptor,
  Component: React.ComponentType<any>,
  options: ChildRouteOptions = {},
) => {
  const { path: override, index: forcedIndex, ...layoutProps } = options;
  const relative =
    override ?? relativePathFromBase(hostExperiencesBase, descriptor.path);
  const isIndex = forcedIndex ?? relative === '';

  const element = (
    <NavigationalLayoutElement {...layoutProps} component={Component as any} />
  );

  if (isIndex) {
    return (
      <Route
        key={`${descriptor.id}__index`}
        id={descriptor.id}
        index
        handle={createRouteHandle(descriptor)}
        element={element}
      />
    );
  }

  return (
    <Route
      key={descriptor.id}
      id={descriptor.id}
      path={relative}
      handle={createRouteHandle(descriptor)}
      element={element}
    />
  );
};

export const hostExperiencesRootRoute: RouteDescriptor = {
  id: 'host-experiences.root',
  path: `${hostExperiencesBase}/*`,
  label: 'Host Experiences Root',
  audience: 'host',
  layout: 'navigational',
  module: 'host-experiences',
};

export const hostExperiencesRecommendationDetailsRoute: RouteDescriptor = {
  id: 'host-experiences.recommendation.detail',
  path: `${hostExperiencesBase}/recommendation/:id`,
  label: 'Host Recommendation Detail',
  audience: 'host',
  layout: 'navigational',
  module: 'host-experiences',
};

export const hostRecommendationsRoute: RouteDescriptor = {
  id: 'host-experiences.recommendations',
  path: `${hostExperiencesBase}/recommendations`,
  label: 'Host Recommendations',
  audience: 'host',
  layout: 'navigational',
  module: 'host-experiences',
};

export const hostExperiencesPausedModalRoute: RouteDescriptor = {
  id: 'host-experiences.paused-modal',
  path: `${hostExperiencesBase}/paused-modal/:itineraryId`,
  label: 'Host Experiences Pause Modal',
  audience: 'host',
  layout: 'modal',
  module: 'host-experiences',
  modal: true,
};

export const hostExperiencesOriginalsRoute: RouteDescriptor = {
  id: 'host-experiences.originals.detail',
  path: `${hostExperiencesBase}/:id`,
  label: 'Host Experience Detail',
  audience: 'host',
  layout: 'navigational',
  module: 'host-experiences',
};

export const hostExperiencesOverviewRoute: RouteDescriptor = {
  id: 'host-experiences.overview',
  path: `${hostExperiencesBase}/`,
  label: 'Host Experiences Overview',
  audience: 'host',
  layout: 'navigational',
  module: 'host-experiences',
};

const SubRoutes = () => {
  const isMobile = useIsMobile();
  return (
    <Routes>
      {createChildRoute(
        hostExperiencesRecommendationDetailsRoute,
        RecommendationDetailsPage,
        {
          path: 'recommendation/:id/*',
          hideFooter: true,
          noNavbar: isMobile,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(hostRecommendationsRoute, HostRecommendationsPage, {
        path: 'recommendations',
        hideFooter: true,
        showBottomNavigation: true,
        noNavbar: isMobile,
        nonFixedNavbar: true,
      })}
      <Route
        key={hostExperiencesPausedModalRoute.id}
        id={hostExperiencesPausedModalRoute.id}
        path="paused-modal/:itineraryId"
        handle={createRouteHandle(hostExperiencesPausedModalRoute)}
        element={<AsyncPausedModal />}
      />
      {createChildRoute(hostExperiencesOriginalsRoute, OriginalsDetailsPage, {
        path: ':id/*',
        hideFooter: true,
        showBottomNavigation: true,
        noNavbar: isMobile,
        nonFixedNavbar: true,
      })}
      {createChildRoute(hostExperiencesOverviewRoute, HostExperiencesPage, {
        index: true,
        hideFooter: true,
        showBottomNavigation: true,
        noNavbar: isMobile,
        nonFixedNavbar: true,
      })}
      <Route path="*" element={<Notfound />} />
    </Routes>
  );
};

const HostExperiencesRoutes = () => (
  <>
    <SubRoutes />
    <AppBottomPushDown />
  </>
);

export const hostExperiencesRoutes = [
  <Route
    key={hostExperiencesRootRoute.id}
    id={hostExperiencesRootRoute.id}
    path={hostExperiencesRootRoute.path}
    handle={createRouteHandle(hostExperiencesRootRoute)}
    element={<HostExperiencesRoutes />}
  />,
];

export const hostExperiencesRouteDescriptors = [
  hostExperiencesRootRoute,
  hostExperiencesRecommendationDetailsRoute,
  hostRecommendationsRoute,
  hostExperiencesPausedModalRoute,
  hostExperiencesOriginalsRoute,
  hostExperiencesOverviewRoute,
];

export default hostExperiencesRoutes;
